import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import * as S from './CollectionDesktopFilter.styles';
import { SearchInput, NothingFound } from './DesktopFilters.styles';
import { getSearchWithoutChildProperties } from './CollectionDesktopFilter.helpers';
import { MIN_OPTIONS_TO_SEARCH } from './CollectionDesktopFilter.constants';
import { DesktopFilterSelectOption } from './DesktopFilterSelectOption';
import { DesktopFilterAccordion } from './DesktopFilterAccordion';
import { Property, PropertyOption } from '@/api/types/categories.types';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { Collection } from '@/components/Filters/FiltersDrawer.types';
import { AccordionRef } from '@/components/UI/Accordion/Accordion';

interface Props {
  propertyLegacyId: string;
  options: PropertyOption[];
  collection: Collection;
  allProperties?: Property[];
  popularOptions?: PropertyOption[];
  filterOpenByDefault?: boolean;
}

export const CollectionDesktopFilter: FC<Props> = ({
  propertyLegacyId,
  options,
  collection,
  allProperties,
  popularOptions,
  filterOpenByDefault,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { clearUrl, queryString } = useUrlAndHashFromPath();
  const [searchValue, setSearchValue] = useState('');
  const accordionRef = useRef<AccordionRef>(null);

  const propertyValue = collection[propertyLegacyId];
  const name = allProperties?.find(
    el => el.legacyResourceId === propertyLegacyId
  )?.name;
  const selected = options.find(
    el => el.legacyResourceId.toString() === propertyValue
  );
  const value = selected?.name;
  const shouldOpenByDefault = !!filterOpenByDefault && !value;

  const searchParamsWithoutChildren = useMemo(
    () =>
      getSearchWithoutChildProperties({
        propertyLegacyId,
        queryString,
        allProperties,
      }),
    [propertyLegacyId, queryString, allProperties]
  );

  function handleClear() {
    const searchParams = new URLSearchParams(searchParamsWithoutChildren);
    searchParams.delete(`p[${propertyLegacyId}]`);
    router.push(`${clearUrl}?${searchParams}`, undefined, {
      scroll: false,
    });
  }

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchValue.toLowerCase())
  );
  const withPopularOptions = !!popularOptions?.length && !searchValue;

  function renderOption({ name: label, legacyResourceId }: PropertyOption) {
    const isSelected = collection?.[propertyLegacyId] === legacyResourceId;
    return (
      <DesktopFilterSelectOption
        key={label}
        label={label}
        hrefParams={{
          clearUrl,
          value: legacyResourceId,
          baseSearchParams: searchParamsWithoutChildren,
          propertyParam: propertyLegacyId,
        }}
        isSelected={isSelected}
        onClick={() => accordionRef.current?.close()}
      />
    );
  }

  if (!name || !options.length) return null;

  return (
    <DesktopFilterAccordion
      key={`${propertyLegacyId}-${String(shouldOpenByDefault)}`}
      ref={accordionRef}
      dataTestId={`collection-property-${propertyLegacyId}`}
      title={name}
      value={value}
      onClear={handleClear}
      defaultOpen={shouldOpenByDefault}
      search={
        options.length >= MIN_OPTIONS_TO_SEARCH && (
          <SearchInput
            $hasBottomMargin={!withPopularOptions}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            placeholder={t('common.search')}
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            type="search"
          />
        )
      }
    >
      {withPopularOptions && (
        <>
          <S.OptionsTitle>{t('common.popular')}</S.OptionsTitle>
          {popularOptions?.map(renderOption)}
          <S.OptionsTitle>{t('common.all')}</S.OptionsTitle>
        </>
      )}

      {filteredOptions.length > 0 ? (
        filteredOptions.map(renderOption)
      ) : (
        <NothingFound>{t('common.nothing_found')}</NothingFound>
      )}
    </DesktopFilterAccordion>
  );
};
